import * as DateF from 'date-fns'
import Numbro from 'numbro'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { UserAvatar } from '@root/entities/auth/ui'
import { DATE_FORMAT } from '@root/shared'
import { Separator } from '@root/shared/ui-kit'

type RankCardProps = {
  rank: number | undefined
  profit: React.ReactNode
  color: 'up' | 'primary'
}

export const RankCard: React.FC<RankCardProps> = ({ rank, profit, color }) => {
  const { t } = useTranslation()
  const dateFormatted = useMemo(
    () => DateF.format(new Date(), DATE_FORMAT.dd_MM_yy),
    [],
  )

  return (
    <Root>
      <UserAvatar size={26} />
      <PlaceContainer>
        {rank ? ( // 0 and undefined
          <>
            <PlaceText>{t('leaderboard.you_are_in')}</PlaceText>
            <Place data-color={color}>
              {Numbro(rank).format({ output: 'ordinal' })}
            </Place>
            <PlaceText>{t('leaderboard.place')}</PlaceText>
          </>
        ) : (
          <PlaceText>{t('leaderboard.dont_have_rank')}</PlaceText>
        )}
      </PlaceContainer>
      <End>
        <DateContainer>{dateFormatted}</DateContainer>
        <Separator vertical />
        <Profit>{profit}</Profit>
      </End>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: auto 1fr auto;
`

const Place = styled.div`
  height: 20px;
  min-width: 24px;
  padding-inline: 8px;
  display: flex;
  align-items: center;

  border-radius: 64px;
  &[data-color='up'] {
    background: var(--upAccent-9);
  }
  &[data-color='primary'] {
    background: var(--primary-9);
    color: var(--primary-contrast);
  }

  color: var(--text-12);
  text-align: center;

  font-size: 10px;
  font-weight: 700;
  line-height: 12px;
`

const PlaceText = styled.div`
  color: var(--text-12);
  font-size: 11px;
  font-weight: 600;
  line-height: 12px;
`

const PlaceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-inline-start: 8px;
`

const DateContainer = styled.div`
  color: var(--textA-7);

  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
`

const End = styled.div`
  padding-left: 12px;
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 10px;
`

const Profit = styled.div`
  color: var(--text-12);
  font-size: 11px;
  font-weight: 600;
  line-height: 12px;
`
